import { ISbStoryData } from "storyblok-js-client";
import { parseLocationUrl, type LocationType } from "@/lib/location";
import { deleteAutoSlug } from "@/utils/autoSlug";
import { isExcludedFolder } from "@/constants/excludedFolders";

export type SitemapUrl = {
  loc: string;
  lastmod?: string;
};

export type SitemapIndexEntry = {
  loc: string;
  lastmod?: string;
};

export type CityInfo = {
  cityId: string;
  regionCode: string;
  citySlug: string;
  story: ISbStoryData;
};

export const SITEMAP_URL_LIMIT = 50_000;

/**
 * Splits a composite cityId back into regionCode and citySlug.
 * Example: "ny-new-york-city-parking" → { regionCode: "ny", citySlug: "new-york-city-parking" }
 */
export function parseCityId(cityId: string): {
  regionCode: string;
  citySlug: string;
} {
  const idx = cityId.indexOf("-");
  return {
    regionCode: cityId.slice(0, idx),
    citySlug: cityId.slice(idx + 1),
  };
}

/**
 * Derives a unique city identifier from a Storyblok full_slug.
 * Format: regionCode-citySlug
 * Example: "parkmobile/parking/locations/ny/new-york-city-parking" → "ny-new-york-city-parking"
 */
export function getCityId(fullSlug: string): string | null {
  const parsed = parseLocationUrl(fullSlug);
  if (!parsed.regionCode || !parsed.citySlug) return null;

  return `${parsed.regionCode}-${parsed.citySlug}`;
}

/**
 * Filters stories to only include indexable ones:
 * - No "no-index" robots directive
 * - Not in excluded folders (in production)
 */
export function filterIndexableStories(
  stories: ISbStoryData[],
  showExcludedFolders: boolean,
): ISbStoryData[] {
  return stories.filter((s) => {
    if (s.content?.robots === "no-index") return false;
    if (!showExcludedFolders && isExcludedFolder(s.full_slug)) return false;

    return true;
  });
}

/**
 * Converts a Storyblok story to a sitemap URL entry.
 */
export function storyToUrl(story: ISbStoryData): SitemapUrl {
  const path = deleteAutoSlug(story.full_slug);

  return {
    loc: `${process.env.NEXT_PUBLIC_DOMAIN}/${path}`,
    lastmod: story.updated_at
      ? new Date(story.updated_at as string).toISOString().split("T")[0]
      : undefined,
  };
}

/**
 * Extracts unique cities from location stories, sorted alphabetically by cityId.
 */
export function extractCities(stories: ISbStoryData[]): CityInfo[] {
  const seen = new Set<string>();
  const cities: CityInfo[] = [];

  for (const story of stories) {
    const { type, regionCode, citySlug } = parseLocationUrl(story.full_slug);
    if (type !== "city") continue;

    const cityId = getCityId(story.full_slug);
    if (!cityId || seen.has(cityId)) continue;

    seen.add(cityId);
    cities.push({
      cityId,
      regionCode: regionCode!,
      citySlug: citySlug!,
      story,
    });
  }

  return cities.sort((a, b) => a.cityId.localeCompare(b.cityId));
}

/**
 * Filters stories by location type and city.
 */
export function filterByTypeAndCity(
  stories: ISbStoryData[],
  locationType: LocationType,
  cityId: string,
): ISbStoryData[] {
  return stories.filter(
    ({ full_slug }) =>
      parseLocationUrl(full_slug).type === locationType &&
      getCityId(full_slug) === cityId,
  );
}

/**
 * Splits an array into pages of the given size.
 */
export function paginate<T>(
  items: T[],
  pageSize: number = SITEMAP_URL_LIMIT,
): T[][] {
  const pages: T[][] = [];

  for (let i = 0; i < items.length; i += pageSize) {
    pages.push(items.slice(i, i + pageSize));
  }

  return pages.length ? pages : [[]];
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function buildUrlsetXml(urls: SitemapUrl[]) {
  const urlEls = urls.map(({ loc, lastmod }) => {
    const lastmodEl = lastmod ? `\n    <lastmod>${lastmod}</lastmod>` : "";
    return `  <url>\n    <loc>${escapeXml(loc)}</loc>${lastmodEl}\n  </url>`;
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...urlEls,
    "</urlset>",
  ].join("\n");
}

export function buildSitemapIndexXml(entries: SitemapIndexEntry[]) {
  const els = entries.map(({ loc, lastmod }) => {
    const lastmodEl = lastmod ? `\n    <lastmod>${lastmod}</lastmod>` : "";
    return `  <sitemap>\n    <loc>${escapeXml(loc)}</loc>${lastmodEl}\n  </sitemap>`;
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...els,
    "</sitemapindex>",
  ].join("\n");
}
